import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
  type User,
} from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { getAdditionalUserInfo, type UserCredential } from "firebase/auth";
import { ACCION, COL, type Rol, type Usuario } from "@zociety/core";
import { auth, db, proveedorApple, proveedorGoogle } from "@/lib/firebase";
import { registrarAccion } from "@/lib/analitica";
import { asegurarPerfil, enviarRestablecimiento, enviarVerificacion } from "@/lib/api";

/**
 * Con Google y Apple no hay dos botones: el mismo sirve para entrar y para
 * registrarse, así que hay que preguntarle al proveedor cuál de las dos fue.
 *
 * Sin esto, todas las altas por Google se contarían como accesos y el número de
 * clientas nuevas saldría en cero justo para el canal que menos fricción tiene.
 */
function esCuentaNueva(cred: UserCredential): boolean {
  return getAdditionalUserInfo(cred)?.isNewUser === true;
}

interface EstadoAuth {
  cuenta: User | null;
  perfil: Usuario | null;
  rol: Rol | null;
  cargando: boolean;
  esStaff: boolean;
  esAdmin: boolean;
  esRecepcion: boolean;
  esCoach: boolean;
  entrar: (email: string, clave: string) => Promise<void>;
  registrar: (email: string, clave: string, nombre: string, apellido: string) => Promise<void>;
  entrarConGoogle: () => Promise<void>;
  entrarConApple: () => Promise<void>;
  salir: () => Promise<void>;
  recuperarClave: (email: string) => Promise<void>;
}

const Ctx = createContext<EstadoAuth | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [cuenta, setCuenta] = useState<User | null>(null);
  const [perfil, setPerfil] = useState<Usuario | null>(null);
  const [rol, setRol] = useState<Rol | null>(null);
  const [cargando, setCargando] = useState(true);

  // El rol se lee del token, que es lo que también evalúan las reglas de
  // Firestore. Así la interfaz y el backend nunca discrepan sobre permisos.
  useEffect(
    () =>
      onIdTokenChanged(auth, async (u) => {
        if (!u) {
          setRol(null);
          return;
        }
        const token = await u.getIdTokenResult();
        setRol((token.claims.rol as Rol) ?? null);
      }),
    []
  );

  useEffect(() => {
    let cancelarPerfil: (() => void) | undefined;

    const cancelarAuth = onAuthStateChanged(auth, (u) => {
      cancelarPerfil?.();
      setCuenta(u);

      if (!u) {
        setPerfil(null);
        setCargando(false);
        return;
      }

      let intentoDeReparacion = false;

      cancelarPerfil = onSnapshot(
        doc(db, COL.USUARIOS, u.uid),
        (snap) => {
          if (!snap.exists() && !intentoDeReparacion) {
            // Cuenta de Auth sin perfil: pasa con usuarios creados desde la
            // consola de Firebase o si algo falló durante el registro. Se
            // repara una sola vez; el onSnapshot recogerá el documento nuevo.
            intentoDeReparacion = true;
            void asegurarPerfil({}).catch(() => undefined);
          }
          if (snap.exists()) {
            const d = snap.data();
            setPerfil({
              uid: snap.id,
              email: d.email ?? u.email ?? "",
              nombre: d.nombre ?? "",
              apellido: d.apellido ?? "",
              telefono: d.telefono ?? "",
              fecha_nacimiento: d.fecha_nacimiento ?? null,
              genero: d.genero,
              rol: d.rol ?? "alumno",
              estado: d.estado ?? "activo",
              fecha_registro_ms: d.fecha_registro?.toMillis?.() ?? 0,
              clases_disponibles: d.clases_disponibles ?? 0,
              faltas_acumuladas: d.faltas_acumuladas ?? 0,
              stats_clases_asistidas: d.stats_clases_asistidas ?? 0,
              stats_clases_programadas: d.stats_clases_programadas ?? 0,
              stats_paquetes_comprados: d.stats_paquetes_comprados ?? 0,
              lesion_activa: d.lesion_activa === true,
              lesion_descripcion: d.lesion_descripcion ?? "",
              lesion_actualizada_ms: d.lesion_actualizada?.toMillis?.() ?? null,
            });
            // Respaldo por si el claim aún no se propagó al token.
            setRol((previo) => previo ?? (d.rol as Rol) ?? "alumno");
          } else {
            setPerfil(null);
          }
          setCargando(false);
        },
        () => setCargando(false)
      );
    });

    return () => {
      cancelarAuth();
      cancelarPerfil?.();
    };
  }, []);

  const valor = useMemo<EstadoAuth>(() => {
    const rolActual = rol ?? perfil?.rol ?? null;
    return {
      cuenta,
      perfil,
      rol: rolActual,
      cargando,
      esStaff: rolActual === "admin" || rolActual === "recepcion" || rolActual === "coach",
      esAdmin: rolActual === "admin",
      esRecepcion: rolActual === "recepcion",
      esCoach: rolActual === "coach",

      entrar: async (email, clave) => {
        await signInWithEmailAndPassword(auth, email.trim().toLowerCase(), clave);
        registrarAccion(ACCION.ACCESO, { method: "password" });
      },

      registrar: async (email, clave, nombre, apellido) => {
        const cred = await createUserWithEmailAndPassword(auth, email.trim().toLowerCase(), clave);

        /*
         * El perfil se pide ANTES de tocar el displayName.
         *
         * `onAuthStateChanged` dispara en cuanto existe la cuenta, ve que no hay
         * perfil y lanza su propia reparación sin nombre. Detenerse aquí en
         * `updateProfile` —que es una ida al servidor— le regalaba la carrera:
         * el perfil nacía vacío y esta llamada llegaba tarde.
         *
         * El backend además completa un perfil sin nombre, así que el orden ya
         * no es lo único que sostiene la corrección.
         */
        await asegurarPerfil({ nombre, apellido });

        const completo = [nombre, apellido].map((p) => p.trim()).filter(Boolean).join(" ");
        await updateProfile(cred.user, { displayName: completo });
        await cred.user.getIdToken(true);

        // Si el correo no sale, el registro ya está hecho y el panel ofrece
        // reenviarlo. Tumbar el alta por un fallo de correo sería peor.
        void enviarVerificacion({}).catch(() => undefined);

        /* Después del alta completa: un registro a medias no es un alta. */
        registrarAccion(ACCION.REGISTRO, { method: "password" });
      },

      // Con Google y Apple el proveedor sí entrega el nombre en el primer
      // token, así que el servidor lo toma de ahí.
      entrarConGoogle: async () => {
        const g = await signInWithPopup(auth, proveedorGoogle);
        await asegurarPerfil({}).catch(() => undefined);
        registrarAccion(esCuentaNueva(g) ? ACCION.REGISTRO : ACCION.ACCESO, { method: "google" });
      },

      entrarConApple: async () => {
        const a = await signInWithPopup(auth, proveedorApple);
        await asegurarPerfil({}).catch(() => undefined);
        registrarAccion(esCuentaNueva(a) ? ACCION.REGISTRO : ACCION.ACCESO, { method: "apple" });
      },

      salir: async () => {
        await signOut(auth);
      },

      /*
       * El correo lo manda el backend desde `@zociety.mx`, no Firebase.
       *
       * `sendPasswordResetEmail` funciona, pero sale de
       * `noreply@zociety-dev.firebaseapp.com`, en inglés y sin la marca. El
       * enlace lo sigue generando Auth —esa parte no se puede falsificar—; lo
       * único que cambia es quién arma y manda el mensaje.
       */
      recuperarClave: async (email) => {
        await enviarRestablecimiento({ email: email.trim().toLowerCase() });
      },
    };
  }, [cuenta, perfil, rol, cargando]);

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useAuth(): EstadoAuth {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth debe usarse dentro de AuthProvider");
  return ctx;
}
